import React, { useMemo } from 'react';
import { Box, Divider, Typography } from '@mui/material';
import ListArticle from './ListArticle';
import { toSourceCategory, toUiPublishState, articleMatchesTargetMediaFilter } from './NewsQueueConstants';
import { useDeclineNews, useGetConfigurations } from '../api/NewsQueueApi';
import RejectDialog from './ArticleView/dialogs/RejectDialog';

const ArticleListRejectController = React.forwardRef(function ArticleListRejectController(_props, ref) {
    const [rejectOpen, setRejectOpen] = React.useState(false);
    const [rejectReason, setRejectReason] = React.useState('');
    const [rejectReasonExtra, setRejectReasonExtra] = React.useState('');
    const [selectedForReject, setSelectedForReject] = React.useState(null);

    const configurationsQuery = useGetConfigurations({ enabled: true });

    const declineNews = useDeclineNews({
        onSuccess: () => {
            setRejectOpen(false);
            setRejectReason('');
            setRejectReasonExtra('');
            setSelectedForReject(null);
        },
    });

    const rejectionReasons = useMemo(() => {
        const cfg = configurationsQuery?.data?.result;
        const raw = cfg?.feedback_categories;
        if (!Array.isArray(raw)) return [];
        return raw.filter((x) => typeof x === 'string' && x.trim().length > 0);
    }, [configurationsQuery?.data?.result]);

    const composedRejectFeedback = useMemo(() => {
        const extra = rejectReasonExtra.trim();
        if (rejectReason === 'other') return extra;

        const base = String(rejectReason ?? '').trim();
        if (!base) return extra;
        if (!extra) return base;
        return `${base}\n\n${extra}`;
    }, [rejectReason, rejectReasonExtra]);

    const openRejectForArticle = React.useCallback(
        (article) => {
            if (!article) return;
            const first = rejectionReasons?.[0] ?? 'other';
            setSelectedForReject(article);
            setRejectReason(first);
            setRejectReasonExtra('');
            setRejectOpen(true);
        },
        [rejectionReasons]
    );

    React.useImperativeHandle(
        ref,
        () => ({
            open: openRejectForArticle,
        }),
        [openRejectForArticle]
    );

    const handleReject = React.useCallback(() => {
        if (!selectedForReject) return;
        declineNews.decline({
            news: selectedForReject,
            feedback: composedRejectFeedback,
        });
    }, [composedRejectFeedback, declineNews, selectedForReject]);

    return (
        <RejectDialog
            open={rejectOpen}
            onClose={() => setRejectOpen(false)}
            rejectReason={rejectReason}
            setRejectReason={setRejectReason}
            rejectReasonExtra={rejectReasonExtra}
            setRejectReasonExtra={setRejectReasonExtra}
            rejectionReasons={rejectionReasons}
            isConfigLoading={configurationsQuery.isLoading}
            isPending={declineNews.isPending}
            isArticleBodyLoading={false}
            isError={declineNews.isError}
            onReject={handleReject}
        />
    );
});

function formatDateLabel(date) {
    const now = new Date();
    const d = new Date(date);

    const isSameDay =
        now.getFullYear() === d.getFullYear() &&
        now.getMonth() === d.getMonth() &&
        now.getDate() === d.getDate();

    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);

    const isYesterday =
        yesterday.getFullYear() === d.getFullYear() &&
        yesterday.getMonth() === d.getMonth() &&
        yesterday.getDate() === d.getDate();

    if (isSameDay) return 'Tänään';
    if (isYesterday) return 'Eilen';
    return d.toLocaleDateString('fi-FI');
}

export default function ArticleList({
    onOpen,
    headerOffset = 0,
    selectedTargetMedias = [],
    selectedCategories = [],
    selectedSources = [],
    selectedStatuses = [],
    onCategoryPick,
    onStatusPick,
    items = [],
    locksById = new Map(),
    newlyInsertedAtById = new Map(),
    sortDirection = 'desc',
    configurations,
}) {
    const rejectControllerRef = React.useRef(null);

    const openRejectForArticle = React.useCallback((article) => {
        rejectControllerRef.current?.open(article);
    }, []);

    const grouped = useMemo(() => {
        const byDate = new Map();
        const filtered = items.filter((item) => {
            const targetMediaOk = articleMatchesTargetMediaFilter(item, selectedTargetMedias);

            const sourceCategory = toSourceCategory(item?.category);

            const categoryOk =
                selectedCategories.length === 0
                    ? true
                    : Boolean(sourceCategory && selectedCategories.includes(sourceCategory));

            const sourceOk = selectedSources.length === 0
                ? true
                : selectedSources.includes(String(item?.source_type ?? '').trim());

            const publishState = toUiPublishState(item?.publish_state);
            const statusOk = selectedStatuses.length === 0
                ? true
                : Boolean(publishState && selectedStatuses.includes(publishState));
            return targetMediaOk && categoryOk && sourceOk && statusOk;
        });

        const sorted = [...filtered].sort((a, b) => {
            const ta = a.timestamp_article ?? a.timestamp_created;
            const tb = b.timestamp_article ?? b.timestamp_created;
            const da = ta ? new Date(ta).getTime() : 0;
            const db = tb ? new Date(tb).getTime() : 0;
            return sortDirection === 'asc' ? da - db : db - da;
        });

        for (const item of sorted) {
            const label = formatDateLabel(item.timestamp_article ?? item.timestamp_created);
            if (!byDate.has(label)) byDate.set(label, []);
            byDate.get(label).push(item);
        }
        return Array.from(byDate.entries());
    }, [
        items,
        selectedTargetMedias,
        selectedCategories,
        selectedSources,
        selectedStatuses,
        sortDirection,
    ]);

    return (
        <Box
            sx={{
                pt: headerOffset,
            }}
        >
            {grouped.map(([label, items]) => (
                <Box
                    key={label}
                    sx={{
                        mb: 3,
                        textAlign: 'left',
                    }}
                >
                    <Box
                        sx={{
                            position: 'sticky',
                            top: headerOffset,
                            zIndex: 1,
                            backgroundColor: 'background.paper',
                            py: 0.5,
                            px: 1,
                            borderBottom: '3px solid #0e57b6',
                            //borderBottom: '1px solid',
                            //borderColor: 'divider',
                        }}
                    >
                        <Typography
                            variant="overline"
                            sx={{
                                color: 'text.secondary',
                            }}
                        >
                            {label}
                        </Typography>
                    </Box>
                    {items.map((article) => (
                        <ListArticle
                            key={
                                article.id ??
                                article.url ??
                                `${article.timestamp_article ?? article.timestamp_created}-${article.article_header}`
                            }
                            article={article}
                            lock={locksById.get(String(article?.id ?? '')) ?? null}
                            newlyInsertedAt={newlyInsertedAtById.get(String(article?.id ?? '')) ?? null}
                            onClick={onOpen}
                            onCategoryPick={onCategoryPick}
                            onStatusPick={onStatusPick}
                            onRejectClick={openRejectForArticle}
                            configurations={configurations}
                        />
                    ))}
                </Box>
            ))}
            <ArticleListRejectController ref={rejectControllerRef} />
        </Box>
    );
}
